import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuArrowLeft, LuRefreshCw } from "react-icons/lu";
import { getProduct } from "../../../../../api/products";
import {
  updateFloatPanel,
  updateFloatPanelData,
} from "../../../../../reducers/userConfigSlice";
import { Details } from "./details";
import { ReferenceOrganization } from "./referenceOrganization";
import { ReferenceAttributes } from "./referenceAttributes";
import { ReferenceVariants } from "./referenceVariants";
import { EditorDialog } from "./editorDialog";
import { Alergens } from "./alergens";

import "./productEditorPage.css";

export const ProductEditorPage = () => {
  const visible = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel.visible,
  );
  return visible ? <ProductEditorContent /> : null;
};

const ProductEditorContent = () => {
  const dispatch = useDispatch();
  const { queryId, isEmpty, data } = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel,
  );
  const [dirty, setDirty] = useState(false);
  const [discard, setDiscard] = useState(false);
  const sections = useRef(new Map());
  const registerSave = useCallback((key, callback) => {
    sections.current.set(key, callback);
    return () => sections.current.delete(key);
  }, []);
  const saveSections = async (productId) => {
    for (const save of sections.current.values()) await save(productId);
  };
  const onDirty = useCallback(() => setDirty(true), []);
  const [tab, setTab] = useState("details");
  const [actionsTarget, setActionsTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!isEmpty);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const loadedId = useRef(null);
  const pageRef = useRef(null);
  const previousFocus = useRef(document.activeElement);
  const close = useCallback(() => {
    if (pageRef.current?.querySelector('[data-editor-write-pending="true"]'))
      return;
    if (dirty) {
      setDiscard(true);
      return;
    }
    dispatch(
      updateFloatPanel({ visible: false, queryId: null, isEmpty: true }),
    );
  }, [dispatch, dirty]);
  useEffect(() => {
    const handleEscape = (event) => {
      if (
        event.key === "Escape" &&
        !event.defaultPrevented &&
        pageRef.current?.contains(event.target)
      ) {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [close]);
  useEffect(() => {
    document.getElementById("adminBody")?.scrollTo(0, 0);
    pageRef.current?.querySelector("button")?.focus();
    const previous = previousFocus.current;
    return () => previous?.focus?.();
  }, []);
  useEffect(() => {
    // A newly created product already contains the response; do not remount its draft fields.
    if (isEmpty) {
      loadedId.current = "creating";
      setLoading(false);
      return;
    }
    if (loadedId.current === "creating" || loadedId.current === queryId) {
      loadedId.current = queryId;
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError("");
    getProduct(queryId)
      .then((response) => {
        if (!active) return;
        if (response?.error !== false || response.data?.productId !== queryId)
          throw new Error("load");
        loadedId.current = queryId;
        dispatch(updateFloatPanelData({ data: response.data }));
      })
      .catch(() => {
        if (active)
          setError("No hemos podido cargar el producto. Vuelve a intentarlo.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [queryId, isEmpty, retry, dispatch]);
  const navigation = [
    ["details", "Detalles principales"],
    ["variants", "Variantes"],
    ["attributes", "Atributos"],
  ];
  return (
    <section
      ref={pageRef}
      className="productEditor"
      aria-label="Ficha de producto"
      onChangeCapture={(event) => {
        if (event.target.closest(".productReferenceDetails") && event.target.id)
          onDirty();
      }}
    >
      <header className="productEditorTop">
        <div className="productEditorTitleRow">
          <button
            type="button"
            className="productEditorBack"
            aria-label="Volver a productos"
            disabled={busy}
            onClick={close}
          >
            <LuArrowLeft aria-hidden="true" />
          </button>
          <div className="productEditorIdentity">
            <h1>{isEmpty ? "Nuevo producto" : data?.name || "Producto"}</h1>
            <span
              className={`productEditorStatus ${data?.status === "PUBLISHED" ? "is-published" : ""}`}
            >
              {data?.status === "PUBLISHED"
                ? "Activado"
                : data?.status === "INACTIVE"
                  ? "Desactivado"
                  : "Borrador"}
              {dirty && (
                <small className="productUnsaved">Cambios sin guardar</small>
              )}
            </span>
          </div>
          <div className="productEditorActionSlot" ref={setActionsTarget} />
        </div>
        <nav className="productEditorTabs" aria-label="Secciones del producto">
          {navigation.map(([id, label]) => (
            <button
              key={id}
              type="button"
              aria-pressed={tab === id}
              disabled={busy || (isEmpty && id !== "details")}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </nav>
      </header>
      {discard && (
        <EditorDialog
          title="¿Salir sin guardar?"
          description="Los cambios pendientes de la ficha se descartarán."
          onClose={() => setDiscard(false)}
          footer={
            <>
              <button
                type="button"
                className="referenceOutline"
                onClick={() => setDiscard(false)}
              >
                Seguir editando
              </button>
              <button
                type="button"
                className="referencePrimary"
                onClick={() =>
                  dispatch(
                    updateFloatPanel({
                      visible: false,
                      queryId: null,
                      isEmpty: true,
                    }),
                  )
                }
              >
                Salir sin guardar
              </button>
            </>
          }
        />
      )}
      {loading ? (
        <p className="productEditorLoad" role="status">
          Cargando producto…
        </p>
      ) : error ? (
        <div role="alert" className="productEditorLoad">
          <p>{error}</p>
          <button
            type="button"
            className="customButton"
            onClick={() => setRetry((value) => value + 1)}
          >
            <LuRefreshCw aria-hidden="true" />
            Reintentar
          </button>
        </div>
      ) : (
        <>
          <div hidden={tab !== "details"}>
            <Details
              referenceLayout
              actionsTarget={actionsTarget}
              onBusyChange={setBusy}
              saveSections={saveSections}
              onSaved={() => setDirty(false)}
              onDirty={onDirty}
              organization={({ draftAllergens, setDraftAllergens, saving }) => (
                <ReferenceOrganization
                  data={data}
                  registerSave={registerSave}
                  onDirty={onDirty}
                  disabled={saving}
                  allergens={
                    <Alergens
                      reference
                      draftValue={draftAllergens}
                      onDraftChange={(value) => {
                        setDraftAllergens(value);
                        onDirty();
                      }}
                      disabled={saving}
                    />
                  }
                />
              )}
            />
          </div>
          {isEmpty && (
            <p className="productEditorHint">
              Guarda el producto antes de configurar atributos y variantes.
            </p>
          )}
          {!isEmpty && (
            <>
              <div hidden={tab !== "variants"} className="productSecondaryCard">
                <ReferenceVariants
                  data={data}
                  registerSave={registerSave}
                  onDirty={onDirty}
                  disabled={busy}
                />
              </div>
              <div
                hidden={tab !== "attributes"}
                className="productSecondaryCard"
              >
                <ReferenceAttributes
                  data={data}
                  registerSave={registerSave}
                  onDirty={onDirty}
                  disabled={busy}
                />
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};
